import { z } from 'zod'

const text = z.string().trim().min(1).max(500)
const note = z.string().trim().max(2000).nullable()
export const financeDateSchema = z.iso.date()
export const financeMoneySchema = z
  .string()
  .regex(/^\d{1,14}(\.\d{1,2})?$/)
  .refine((value) => /[1-9]/.test(value))
export const financeQuantitySchema = z
  .string()
  .regex(/^\d{1,14}(\.\d{1,6})?$/)
  .refine((value) => /[1-9]/.test(value))
export const financeVersionSchema = z
  .object({ id: text, version: z.number().int().positive() })
  .strict()
export const invoiceCreateSchema = z
  .object({
    number: text,
    kind: z.enum(['full', 'prepayment', 'balance', 'other']),
    sellerEntityId: text,
    customerId: text,
    dealId: text.nullable(),
    currency: z.enum(['UAH', 'EUR', 'USD']),
    vatName: text,
    vatRate: z.string().regex(/^(0|20|7|14)(\.0+)?$/),
    lines: z
      .array(
        z
          .object({
            description: text,
            quantity: financeQuantitySchema,
            unit: text,
            unitPrice: z.string().regex(/^\d{1,14}(\.\d{1,2})?$/),
          })
          .strict(),
      )
      .min(1)
      .max(200),
    dueAt: financeDateSchema,
    comment: note,
  })
  .strict()
export const invoiceSentSchema = financeVersionSchema.extend({
  via: z.enum(['Email', 'Viber', 'Telegram', 'Особисто']),
})
export const invoicePaymentSchema = financeVersionSchema.extend({
  amount: financeMoneySchema,
  at: financeDateSchema,
  method: z.enum(['bank', 'cash']),
  reference: note,
})
export const invoiceReverseSchema = financeVersionSchema.extend({
  paymentId: text,
  reason: z.string().trim().min(5).max(2000),
})
export const paymentRequestCreateSchema = z
  .object({
    supplier: text,
    purpose: text,
    amount: financeMoneySchema,
    dueAt: financeDateSchema,
    buyerEntityId: text,
    invoice: note,
    comment: note,
  })
  .strict()
export const paymentRequestDecisionSchema = financeVersionSchema.extend({
  status: z.enum(['approved', 'rejected']),
  reason: z.string().trim().max(2000).optional(),
})
export const paymentRequestTransitionSchema = financeVersionSchema.extend({
  status: z.enum(['pending', 'approved', 'rejected', 'paid']),
  reason: z.string().trim().min(5).max(2000),
})
export const paymentRequestPaidSchema = financeVersionSchema.extend({
  paidAt: financeDateSchema,
  reference: note,
})
export const paymentRequestUndoSchema = financeVersionSchema.extend({
  reason: z.string().trim().min(5).max(2000),
})
export const foreignPaymentCreateSchema = z
  .object({
    supplier: text,
    payerCompanyId: text,
    invoiceNumber: text,
    amount: financeMoneySchema,
    currency: z.enum(['EUR', 'USD', 'UAH']),
    dueDate: financeDateSchema,
    beneficiary: text,
    bank: text,
    iban: text,
    swift: text,
  })
  .strict()
export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>
export type PaymentRequestCreateInput = z.infer<typeof paymentRequestCreateSchema>
export type ForeignPaymentCreateInput = z.infer<typeof foreignPaymentCreateSchema>

export const financeInvoiceTotal = (
  lines: readonly { quantity: string; unitPrice: string }[],
): string => {
  const scaled = (value: string, places: number) => {
    if (!/^\d*(\.\d*)?$/.test(value)) return 0n
    const [whole = '', fraction = ''] = value.split('.')
    return (
      BigInt(whole || '0') * 10n ** BigInt(places) +
      BigInt(fraction.padEnd(places, '0').slice(0, places))
    )
  }
  const total = lines.reduce(
    (sum, line) => sum + scaled(line.quantity, 6) * scaled(line.unitPrice, 2),
    0n,
  )
  const cents = (total + 500000n) / 1000000n
  return `${cents / 100n}.${String(cents % 100n).padStart(2, '0')}`
}
